import {
  DateTimeOffset,
  MappedReservedTimes,
} from 'components/Form/BDatePicker';
import { FormAction } from 'hooks/useForm';
import { Dispatch, useEffect, useMemo } from 'react';
import { dateService } from 'services/DateService';
import {
  DemandResponseDemand,
  VehicleBasedDemandState,
} from 'types/demand.type';
import { HourlyRentalInfo } from 'types/vehicle.type';
import { usePriceWidgetClosestAvailability } from './usePriceWidgetClosestAvailability';
import { usePriceWidgetIsSpecialDate } from './usePriceWidgetIsSpecialDate';

export function usePriceWidgetDurationLimits(
  state: VehicleBasedDemandState,
  dispatch: Dispatch<FormAction<VehicleBasedDemandState>>,
  rentalInfo: HourlyRentalInfo,
  mappedReservedTimes: MappedReservedTimes,
  datePickerDisabledHours: DateTimeOffset[],
  demandData?: DemandResponseDemand
) {
  const dayjs = dateService.getDayjs();
  const { getIsSpecialDate } = usePriceWidgetIsSpecialDate(rentalInfo);
  const { constructSchedule, findFirstUnavailableHour } =
    usePriceWidgetClosestAvailability(
      state,
      dispatch,
      mappedReservedTimes,
      datePickerDisabledHours,
      demandData
    );

  const MIN_DURATION = useMemo(() => {
    if (state.isSwimmingTour) {
      return 8;
    }
    if (state.date && rentalInfo.specialDayFactors) {
      const isSpecialDate = getIsSpecialDate(state.date);
      if (isSpecialDate) {
        return rentalInfo.minimumRentDurationOnSpecialDays;
      }
    }
    return rentalInfo.minimumRentDuration ?? 1;
  }, [state.duration, state.date, state.isSwimmingTour]);

  const MAX_DURATION = useMemo(() => {
    if (state.isSwimmingTour) {
      return 8;
    }
    let maxDuration = 12;
    let minLimit = rentalInfo.minimumRentDuration;

    if (state.date) {
      const isSpecialDate = getIsSpecialDate(state.date);
      minLimit = isSpecialDate
        ? rentalInfo.minimumRentDurationOnSpecialDays
        : rentalInfo.minimumRentDuration;
      const currentHour = dayjs(state.date).hour();
      const todaysSchedule = constructSchedule(state.date);
      const firstFilledHourAfterCurrentHourToday = findFirstUnavailableHour(
        todaysSchedule,
        currentHour
      );

      if (firstFilledHourAfterCurrentHourToday) {
        maxDuration = firstFilledHourAfterCurrentHourToday.hour - currentHour;
      } else {
        const tomorrow = dayjs(state.date).add(1, 'day').toDate();
        const tomorrowsSchedule = constructSchedule(tomorrow);
        const firstFilledHourAfterCurrentHourTomorrow = tomorrowsSchedule?.find(
          (d) => !d.isOffset
        );
        if (firstFilledHourAfterCurrentHourTomorrow) {
          const firstAvailableHour =
            24 + firstFilledHourAfterCurrentHourTomorrow.hour - currentHour;
          maxDuration = firstAvailableHour;
        }
      }
    }
    return Math.min(Math.max(maxDuration, minLimit), 12);
  }, [state.isSwimmingTour, state.date]);

  useEffect(() => {
    if (MIN_DURATION > state.duration) {
      dispatch({
        type: 'duration',
        payload: MIN_DURATION,
      });
    }
  }, [MIN_DURATION]);

  useEffect(() => {
    if (MAX_DURATION < state.duration) {
      dispatch({
        type: 'duration',
        payload: MAX_DURATION,
      });
    }
  }, [MAX_DURATION]);

  return {
    MIN_DURATION,
    MAX_DURATION,
  };
}
